import { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { FloppyDisk, PaperPlaneTilt, Eye, TextAa, Code, PencilSimple } from "@phosphor-icons/react";
import RichTextEditor from "@/components/RichTextEditor";

const EMPTY_FORM = {
  trigger: "lead_assigned",
  name: "",
  subject: "",
  body_html: "<p></p>",
  to: "",
  cc: "",
  bcc: "",
  enabled: true,
  tags: "",
};

// Triggers whose primary recipient is auto-resolved by the system (e.g. the
// assignee for `lead_assigned`). For these we hide the To field. Every other
// trigger needs an explicit To list configured by the admin — most importantly
// `lead_new`, which is the "every new lead" announcement.
const AUTO_TO_TRIGGERS = new Set([
  "lead_assigned",
  "lead_status_won",
  "lead_status_lost",
  "lead_status_contacted",
  "lead_status_in_progress",
  "sla_breach_first_response",
  "sla_breach_close",
]);

const MODES = { VISUAL: "visual", HTML: "html", PREVIEW: "preview" };

export default function TemplateEditor({ template, triggers, placeholders, onClose, onSaved }) {
  const isEdit = !!template;
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [mode, setMode] = useState(MODES.VISUAL);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const subjectRef = useRef(null);
  const richRef = useRef(null);
  const [lastFocused, setLastFocused] = useState("body");

  useEffect(() => {
    if (template) {
      setForm({
        trigger: template.trigger,
        name: template.name,
        subject: template.subject,
        body_html: template.body_html || "<p></p>",
        to: template.to || "",
        cc: template.cc || "",
        bcc: template.bcc || "",
        enabled: template.enabled,
        tags: (template.tags || []).join(", "),
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setMode(MODES.VISUAL);
  }, [template]);

  const insertPlaceholder = (key) => {
    const insertion = `{{${key}}}`;
    if (lastFocused === "subject" && subjectRef.current) {
      const el = subjectRef.current;
      const start = el.selectionStart ?? form.subject.length;
      const end = el.selectionEnd ?? form.subject.length;
      const next = form.subject.slice(0, start) + insertion + form.subject.slice(end);
      setForm({ ...form, subject: next });
      setTimeout(() => { el.focus(); el.setSelectionRange(start + insertion.length, start + insertion.length); }, 0);
      return;
    }
    // Body - depends on mode
    if (mode === MODES.VISUAL && richRef.current) {
      richRef.current.insertAtCursor(insertion);
    } else if (mode === MODES.HTML) {
      setForm((f) => ({ ...f, body_html: (f.body_html || "") + insertion }));
    }
  };

  const buildPayload = () => ({
    trigger: form.trigger,
    name: form.name.trim(),
    subject: form.subject,
    body_html: form.body_html,
    to: form.to.trim() || null,
    cc: form.cc.trim() || null,
    bcc: form.bcc.trim() || null,
    enabled: form.enabled,
    tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
  });

  const onSave = async () => {
    if (!form.name.trim() || !form.subject.trim()) {
      toast.error("Name and subject are required");
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        await api.patch(`/email/templates/${template.id}`, payload);
      } else {
        await api.post(`/email/templates`, payload);
      }
      toast.success(isEdit ? "Template updated" : "Template created");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onPreview = async () => {
    // Render placeholders against a sample lead
    if (!isEdit) {
      // Local render (no template ID yet) — replace placeholders with sample values
      const sample = {
        "lead.name": "Nato Swanepoel",
        "lead.first_name": "Nato",
        "lead.phone": "+27 82 781 1644",
        "lead.email": "nato.swanepoel@gmail.com",
        "lead.summary": "Relating to medical aid claims being investigated.",
        "lead.source": "Website Contact Form",
        "lead.urgency": "Today",
        "lead.status": "New",
        "lead.email_subject": "Fw: New Contact Form submission",
        "lead.received_at": "2026-05-08",
        "lead.url": "#",
        "attorney.name": "George Herbst",
        "attorney.first_name": "George",
        "attorney.email": "george@barnardinc.co.za",
        "attorney.job_title": "Director — Litigation",
        "firm.name": "Barnard Inc",
        "user.name": "Mark Beckman",
        "user.email": "mark@barnardinc.co.za",
      };
      const replace = (str) => (str || "").replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, k) => sample[k] ?? "");
      setPreviewSubject(replace(form.subject));
      setPreviewHtml(replace(form.body_html));
      setMode(MODES.PREVIEW);
      return;
    }
    try {
      await api.patch(`/email/templates/${template.id}`, buildPayload());
      const { data } = await api.post(`/email/preview`, { template_id: template.id, to: "preview@example.com" });
      setPreviewSubject(data.subject);
      setPreviewHtml(data.body_html);
      setMode(MODES.PREVIEW);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const onSendTest = async () => {
    if (!isEdit) {
      toast.error("Save the template first before sending a test");
      return;
    }
    if (!testEmail.trim()) {
      toast.error("Enter a recipient email");
      return;
    }
    setSending(true);
    try {
      await api.patch(`/email/templates/${template.id}`, buildPayload());
      await api.post(`/email/test`, { template_id: template.id, to: testEmail.trim() });
      toast.success(`Test sent to ${testEmail}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const triggerInfo = triggers.find((t) => t.key === form.trigger);

  const ModeBtn = ({ value, icon: Icon, label, testId }) => (
    <button
      type="button"
      onClick={() => setMode(value)}
      data-testid={testId}
      className={
        "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm border transition-colors " +
        (mode === value
          ? "bg-slate-900 text-white border-slate-900"
          : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100")
      }
    >
      <Icon size={12} weight={mode === value ? "fill" : "regular"} />
      {label}
    </button>
  );

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="template-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isEdit ? "Edit Template" : "New Template"}
          </DialogTitle>
          <DialogDescription>
            Triggered automatically when the matching event fires. Write your email like in Outlook — formatting is preserved.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
          {/* Form */}
          <div className="space-y-4 min-w-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="label-tracking">Template Name</Label>
                <Input
                  data-testid="tpl-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. New lead assigned"
                  className="mt-2 rounded-sm h-10"
                />
              </div>
              <div>
                <Label className="label-tracking">Trigger Event</Label>
                <Select value={form.trigger} onValueChange={(v) => setForm({ ...form, trigger: v })}>
                  <SelectTrigger data-testid="tpl-trigger-select" className="mt-2 rounded-sm h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {triggers.map((t) => (
                      <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {triggerInfo && <p className="text-[11px] text-slate-500 mt-1">{triggerInfo.description}</p>}
              </div>
              <div data-testid="tpl-to-row">
                <Label className="label-tracking">
                  To (comma-separated)
                  {AUTO_TO_TRIGGERS.has(form.trigger) && (
                    <span className="ml-2 normal-case tracking-normal text-[10px] text-slate-400">optional — leave blank for the system default</span>
                  )}
                </Label>
                <Input
                  data-testid="tpl-to-input"
                  value={form.to}
                  onChange={(e) => setForm({ ...form, to: e.target.value })}
                  placeholder={
                    AUTO_TO_TRIGGERS.has(form.trigger)
                      ? "Leave blank for the default (e.g. the assignee). Or override / add explicit recipients."
                      : "e.g. partners@barnardinc.co.za, jaco.els@barnardinc.co.za"
                  }
                  className="mt-2 rounded-sm h-10 font-mono text-xs"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  {AUTO_TO_TRIGGERS.has(form.trigger)
                    ? <>By default this trigger emails the {triggerInfo?.default_recipient || "auto-resolved recipient"}. Add addresses here to <strong>override</strong> the default and send to a fixed list instead.</>
                    : <>Required. First address is the primary To; the rest are added to CC. <span className="font-mono">{form.trigger}</span> has no auto-resolved recipient.</>}
                </p>
              </div>
              <div>
                <Label className="label-tracking">CC (optional, comma-separated)</Label>
                <Input
                  data-testid="tpl-cc-input"
                  value={form.cc}
                  onChange={(e) => setForm({ ...form, cc: e.target.value })}
                  className="mt-2 rounded-sm h-10 font-mono text-xs"
                />
              </div>
              <div>
                <Label className="label-tracking">BCC (optional)</Label>
                <Input
                  data-testid="tpl-bcc-input"
                  value={form.bcc}
                  onChange={(e) => setForm({ ...form, bcc: e.target.value })}
                  className="mt-2 rounded-sm h-10 font-mono text-xs"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="label-tracking">Tags (comma-separated)</Label>
                <Input
                  data-testid="tpl-tags-input"
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  placeholder="e.g. assignment, urgent, default"
                  className="mt-2 rounded-sm h-10 text-sm"
                />
              </div>
            </div>

            <div>
              <Label className="label-tracking">Subject</Label>
              <Input
                ref={subjectRef}
                data-testid="tpl-subject-input"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                onFocus={() => setLastFocused("subject")}
                placeholder="New lead allocated to you: {{lead.name}}"
                className="mt-2 rounded-sm h-10 text-sm"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="label-tracking">Body</Label>
                <div className="flex items-center gap-1" data-testid="mode-switcher">
                  <ModeBtn value={MODES.VISUAL} icon={TextAa} label="Visual" testId="mode-visual-btn" />
                  <ModeBtn value={MODES.HTML} icon={Code} label="HTML" testId="mode-html-btn" />
                  <button
                    type="button"
                    onClick={onPreview}
                    data-testid="mode-preview-btn"
                    className={
                      "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm border transition-colors " +
                      (mode === MODES.PREVIEW
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100")
                    }
                  >
                    <Eye size={12} weight={mode === MODES.PREVIEW ? "fill" : "regular"} />
                    Preview
                  </button>
                </div>
              </div>

              <div onFocusCapture={() => setLastFocused("body")}>
                {mode === MODES.VISUAL && (
                  <RichTextEditor
                    ref={richRef}
                    value={form.body_html}
                    onChange={(v) => setForm((f) => ({ ...f, body_html: v }))}
                    placeholder="Write your email. Click a placeholder on the right to insert it."
                    testId="rich-editor"
                  />
                )}
                {mode === MODES.HTML && (
                  <Textarea
                    data-testid="tpl-body-html-textarea"
                    value={form.body_html}
                    onChange={(e) => setForm({ ...form, body_html: e.target.value })}
                    rows={14}
                    className="rounded-sm font-mono text-xs leading-relaxed"
                  />
                )}
                {mode === MODES.PREVIEW && (
                  <div className="border border-slate-300 rounded-sm bg-white" data-testid="preview-pane">
                    <div className="border-b border-slate-200 p-3 bg-slate-50">
                      <div className="text-[11px] text-slate-500 mb-1">Subject preview:</div>
                      <div className="font-medium text-sm">{previewSubject}</div>
                    </div>
                    <div className="p-4 max-h-[400px] overflow-y-auto prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml, { USE_PROFILES: { html: true } }) }} />
                  </div>
                )}
              </div>

              <p className="text-[11px] text-slate-400 mt-2">
                {mode === MODES.VISUAL && "Write like in Outlook — bold, italic, lists, links. Placeholders insert at your cursor."}
                {mode === MODES.HTML && "Advanced — edit raw HTML. Switch back to Visual when done."}
                {mode === MODES.PREVIEW && "Showing how this email looks when sent with sample data."}
              </p>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-sm border border-slate-200">
              <div>
                <div className="text-sm font-medium text-slate-900">Enable this template</div>
                <div className="text-xs text-slate-500">If off, this template will not be used for the trigger.</div>
              </div>
              <Switch
                data-testid="tpl-enabled-switch"
                checked={form.enabled}
                onCheckedChange={(v) => setForm({ ...form, enabled: v })}
              />
            </div>

            {/* Send test */}
            <div className="border-t border-slate-200 pt-4 mt-4">
              <Label className="label-tracking">Send a test email</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  data-testid="tpl-test-email-input"
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@barnardinc.co.za"
                  className="rounded-sm h-10 font-mono text-xs"
                />
                <Button
                  data-testid="tpl-send-test-btn"
                  onClick={onSendTest}
                  disabled={sending || !isEdit}
                  variant="outline"
                  className="rounded-sm gap-2 shrink-0"
                >
                  <PaperPlaneTilt size={14} />
                  {sending ? "Sending…" : "Send test"}
                </Button>
              </div>
              {!isEdit && <p className="text-[11px] text-slate-500 mt-1">Save the template first to enable testing.</p>}
            </div>
          </div>

          {/* Placeholders sidebar */}
          <aside className="border-l border-slate-200 lg:pl-6">
            <div className="label-tracking mb-3">Placeholders</div>
            <p className="text-[11px] text-slate-500 mb-3">Click a chip to insert it where your cursor is.</p>
            <div className="space-y-1 max-h-[500px] overflow-y-auto pr-2" data-testid="placeholder-list">
              {placeholders.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => insertPlaceholder(p.key)}
                  className="w-full text-left p-2 rounded-sm hover:bg-slate-100 group transition-colors"
                  data-testid={`placeholder-${p.key}`}
                >
                  <div className="font-mono text-[11px] text-[#002FA7] group-hover:text-slate-900">
                    {`{{${p.key}}}`}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{p.label}</div>
                </button>
              ))}
            </div>
          </aside>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button
            data-testid="tpl-save-btn"
            onClick={onSave}
            disabled={saving}
            className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2"
          >
            <FloppyDisk size={14} />
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
